import { DialogContent, Stack, Box, Typography } from "@mui/material";
import { useState, useMemo } from "react";

import { useTranslation } from "../../../../hooks/useTranslation";
import { OrderDetail } from "../../types";
import type { EditedItem, EditedDimensions } from "./PrepareShipmentModal";

import { DimensionsSection } from "./components/DimensionsSection";
import { ItemList } from "./components/ItemList";
import { Actions } from "./components/Actions";

type Props = {
  orderDetails: OrderDetail;
  onNext: (_items: EditedItem[], _dimensions: EditedDimensions) => void;
  onClose: () => void;
};

export const DimensionsAndPriceStep = ({
  orderDetails,
  onNext,
  onClose,
}: Props) => {
  const { translate } = useTranslation("pages.orderDetails.prepareShipment");

  /* =====================================================
     DIMENSIONS STATE
  ===================================================== */
  const [dimensions, setDimensions] = useState<EditedDimensions>({
    length: orderDetails.estimatedLength?.value?.toString() ?? "",
    width: orderDetails.estimatedWidth?.value?.toString() ?? "",
    height: orderDetails.estimatedHeight?.value?.toString() ?? "",
    weight: orderDetails.estimatedWeight?.value?.toString() ?? "",
  });

  const units = useMemo(
    () => ({
      length: orderDetails.estimatedLength?.unit ?? "",
      width: orderDetails.estimatedWidth?.unit ?? "",
      height: orderDetails.estimatedHeight?.unit ?? "",
      weight: orderDetails.estimatedWeight?.unit ?? "",
    }),
    [orderDetails],
  );

  const isPositiveNumber = (v: string) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0;
  };

  const dimensionsValid = Object.values(dimensions).every(isPositiveNumber);

  /* =====================================================
     ITEMS STATE
  ===================================================== */
  const [includedItems, setIncludedItems] = useState<Record<number, boolean>>(
    () =>
      orderDetails.orderItems.reduce(
        (acc, item) => {
          acc[item.id] = true;
          return acc;
        },
        {} as Record<number, boolean>,
      ),
  );

  const [totalPrices, setTotalPrices] = useState<Record<number, string>>(() =>
    orderDetails.orderItems.reduce(
      (acc, item) => {
        acc[item.id] = "";
        return acc;
      },
      {} as Record<number, string>,
    ),
  );

  const [quantities, setQuantities] = useState<Record<number, string>>(() =>
    orderDetails.orderItems.reduce(
      (acc, item) => {
        acc[item.id] = item.quantity.toString();
        return acc;
      },
      {} as Record<number, string>,
    ),
  );

  /* =====================================================
     OVER SHIPMENT
  ===================================================== */
  const [allowOverShipment, setAllowOverShipment] = useState(false);

  const getMaxQuantity = (_item: OrderDetail["orderItems"][number]) =>
    Infinity;

  const isQuantityValid = (
    _item: OrderDetail["orderItems"][number],
    value: string,
  ) => {
    const n = Number(value);
    return Number.isInteger(n) && n >= 0;
  };

  const isPriceValid = (value: string) => {
    if (value.trim() === "") return false;
    return Number.isFinite(Number(value));
  };

  const pricesValid = orderDetails.orderItems.every(
    (item) => !includedItems[item.id] || isPriceValid(totalPrices[item.id]),
  );

  const quantitiesValid = orderDetails.orderItems.every(
    (item) =>
      !includedItems[item.id] || isQuantityValid(item, quantities[item.id]),
  );

  const hasAtLeastOneItemToShip = orderDetails.orderItems.some(
    (item) => includedItems[item.id] && Number(quantities[item.id]) > 0,
  );

  const isFormValid =
    dimensionsValid &&
    pricesValid &&
    quantitiesValid &&
    hasAtLeastOneItemToShip;

  /* =====================================================
     HANDLERS
  ===================================================== */
  const handleNext = () => {
    if (!isFormValid) return;

    const items: EditedItem[] = orderDetails.orderItems.map((item) => {
      const qty = includedItems[item.id] ? Number(quantities[item.id]) : 0;
      const total = Number(totalPrices[item.id]);
      const unitPrice = qty > 0 ? total / qty : 0;
      return { id: item.id, price: unitPrice, quantity: qty };
    });

    onNext(items, dimensions);
  };

  /* =====================================================
     UI
  ===================================================== */
  return (
    <>
      <DialogContent>
        <Stack spacing={4}>
          {/* ================= DIMENSIONS ================= */}
          <DimensionsSection
            dimensions={dimensions}
            units={units}
            isPositiveNumber={isPositiveNumber}
            onChange={(field, value) =>
              setDimensions((prev) => ({
                ...prev,
                [field]: value,
              }))
            }
          />

          {/* ================= ITEMS ================= */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" mb={1}>
              {translate("subTitle")}
            </Typography>

            {!hasAtLeastOneItemToShip && (
              <Box mb={2}>
                <Typography color="warning.main">
                  {translate("atLeastOneItemMustBeShipped")}
                </Typography>
              </Box>
            )}

            <ItemList
              orderItems={orderDetails.orderItems}
              includedItems={includedItems}
              quantities={quantities}
              totalPrices={totalPrices}
              allowOverShipment={allowOverShipment}
              isQuantityValid={isQuantityValid}
              isPriceValid={isPriceValid}
              onIncludeChange={(id, checked) => {
                setIncludedItems((prev) => ({
                  ...prev,
                  [id]: checked,
                }));
                if (!checked) {
                  setQuantities((prev) => ({
                    ...prev,
                    [id]: "0",
                  }));
                }
              }}
              onQuantityChange={(id, value) => {
                setQuantities((prev) => ({ ...prev, [id]: value }));
              }}
              onTotalPriceChange={(id, value) => {
                setTotalPrices((prev) => ({ ...prev, [id]: value }));
              }}
            />
          </Box>
        </Stack>
      </DialogContent>

      {/* ================= ACTIONS ================= */}
      <Actions
        allowOverShipment={allowOverShipment}
        onAllowOverShipmentChange={setAllowOverShipment}
        onClose={onClose}
        onNext={handleNext}
        isFormValid={isFormValid}
      />
    </>
  );
};
